import { displayedEmail, displayedPhone } from '@/app/(main)/client-detail/utils/contacts';
import { getAge } from '@/app/(main)/clients/create/utils/getAge';
import type { GetClientData } from '@/types/clientsApiTypes';
import type { Nullable, Optional } from '@/types/common';
import { getAgeFromIsoDate } from '@/utils/parseIsoDateSafe';

export type AdultPrimaryPassengerCandidate = Pick<
  GetClientData,
  'id' | 'full_name' | 'first_name' | 'last_name' | 'date_of_birth' | 'email' | 'phone' | 'is_primary'
>;

export type EligibleAdultPrimaryPassenger = {
  id: number;
  label: string;
  email?: Nullable<string>;
  phone?: Nullable<string>;
};

interface GetEligibleAdultPrimaryPassengersParams {
  currentClient?: Optional<AdultPrimaryPassengerCandidate>;
  relatedTravelers: AdultPrimaryPassengerCandidate[];
  excludeClientId?: Optional<number>;
}

function getPassengerAge(dateOfBirth: Optional<string>): Nullable<number> {
  if (!dateOfBirth) {
    return null;
  }

  if (dateOfBirth.includes('/')) {
    return getAge(dateOfBirth);
  }

  return getAgeFromIsoDate(dateOfBirth);
}

function isAdult(dateOfBirth: Optional<string>): boolean {
  const age = getPassengerAge(dateOfBirth);
  return age !== null && age >= 18;
}

function getPassengerLabel(passenger: AdultPrimaryPassengerCandidate): string {
  const fullName = passenger.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const combined = `${passenger.first_name?.trim() ?? ''} ${passenger.last_name?.trim() ?? ''}`.trim();
  return combined || 'Primary Passenger';
}

export function getEligibleAdultPrimaryPassengers({
  currentClient,
  relatedTravelers,
  excludeClientId,
}: GetEligibleAdultPrimaryPassengersParams): EligibleAdultPrimaryPassenger[] {
  const candidates: AdultPrimaryPassengerCandidate[] = [];

  if (currentClient) {
    candidates.push(currentClient);
  }

  relatedTravelers.forEach((traveler) => {
    if (traveler.is_primary) {
      candidates.push(traveler);
    }
  });

  const uniqueById = new Map<number, EligibleAdultPrimaryPassenger>();

  candidates.forEach((candidate) => {
    if (candidate.id === excludeClientId) {
      return;
    }

    if (!isAdult(candidate.date_of_birth)) {
      return;
    }

    if (uniqueById.has(candidate.id)) {
      return;
    }

    uniqueById.set(candidate.id, {
      id: candidate.id,
      label: getPassengerLabel(candidate),
      email: displayedEmail(candidate) || null,
      phone: displayedPhone(candidate) || null,
    });
  });

  return [...uniqueById.values()];
}